import { customers as customersClient, unwrap, ApiError } from "../api";
import type { PagedResult } from "../../components/table/data-table";
import { customerPath, isGuid, redirectToCanonical } from "../paths";

/**
 * Ported from apps/admin/app/(app)/customers/{page,actions}.tsx + ageing/dedupe/trade-terms pages.
 * Every gateway path here matches what apps/admin/lib/api.ts's customers()/customersWritable()
 * client hit — verified against the Next source, not guessed.
 */

export type CustomerSummary = {
  customerId: string;
  /** `CUS-000123` — the customer's URL key (`customerPath`); immutable, not personal data. */
  customerNumber: string;
  displayName: string;
  /** The API sends `CustomerStatus` as a snake_case string (`"active"`); the generated contract says number — see `customerStatusLabel`. */
  status: number | string;
  isDefaultTier: boolean;
  creditLimit: number | string | null;
  outstanding: number | string;
  tallyLedgerLinked: boolean;
  excludesReceipts?: boolean;
  receiptsLastAppliedAt?: string | null;
  /** GAP-7: `outstanding > creditLimit` (`CustomerBalance.OverLimit`) — the API's own comparison, never re-derived here (003 T035). */
  overLimit: boolean;
  /** Numeric in the generated contract; the live API sends `"company"`. */
  type?: number | string;
  tierId?: string | null;
  /** Catalog's name for `tierId`, sent by Customers; null when unresolved. */
  tierName?: string | null;
};

/**
 * The list/tile label for how a customer buys. BR-01: the tier decides it — a company still on the
 * Default Tier buys at piece prices, prepaid, until a tier is assigned. Labelled B2B regardless
 * (owner, 2026-10-16), with the step still owed, so a registered company never reads as retail.
 */
export function basisLabel(customer: Pick<CustomerSummary, "isDefaultTier" | "type">): string {
  if (!customer.isDefaultTier) return "Unit rates · B2B";
  return customer.type === "company" ? "Awaiting trade tier · B2B" : "Piece prices · B2C";
}

export type PagedCustomers = {
  items: CustomerSummary[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages?: number;
};

/** `AddressType.cs`: Billing=0, Shipping=1 — see `addressTypeLabel`. */
export type CustomerAddress = { id?: string; type: number; line1?: string; line2?: string | null; city?: string; stateCode?: string | null; pincode?: string; isDefault?: boolean };
export type CustomerUser = { id: string; name: string; email: string; isActive: boolean; role?: number | string; orderValueLimit?: number | string | null };

/**
 * `CustomerStatus.cs`'s wire spelling. The API serialises the enum as a snake_case string
 * (`CustomersJsonOptions`: `JsonStringEnumConverter(SnakeCaseLower)`), so a string is already the
 * label; the numeric map covers the filter's URL value (the query binds the enum's number).
 */
const CUSTOMER_STATUS_LABEL: Readonly<Record<number, string>> = {
  0: "pending_verification",
  1: "active",
  2: "suspended",
  3: "closed",
};
export function customerStatusLabel(status: number | string): string {
  return typeof status === "string" ? status : (CUSTOMER_STATUS_LABEL[status] ?? String(status));
}

/** `AddressType.cs`'s own "Wire: `<value>`" doc comment on each member — display label only. */
const ADDRESS_TYPE_LABEL: Readonly<Record<number, string>> = {
  0: "billing",
  1: "shipping",
};
export function addressTypeLabel(type: number): string {
  return ADDRESS_TYPE_LABEL[type] ?? String(type);
}

export type CustomerDetail = {
  legalName: string;
  gstin: string | null;
  email?: string | null;
  registeredMobile?: string | null;
  paymentTermDays: number | null;
  availableCredit: number | string | null;
  tradeTermsRequestStatus: string | null;
  /** The open trade-terms request's id, so terms assigned from the detail page close it (FR-030). */
  pendingTradeTermsRequestId?: string | null;
  pan?: string | null;
  divergesFromAccounting: boolean;
  addresses: CustomerAddress[];
  users: CustomerUser[];
  summary: CustomerSummary;
};

export async function listCustomers(params: { page: number; pageSize?: number; q?: string; status?: string; tierId?: string }) {
  return unwrap(
    (await customersClient()).GET("/api/customers/admin/customers", {
      params: {
        query: {
          page: params.page,
          pageSize: params.pageSize ?? 50,
          q: params.q,
          // `CustomerStatus` is numeric on the wire (see `customerStatusLabel`); the URL carries the number.
          status: params.status === undefined ? undefined : Number(params.status),
          tierId: params.tierId,
        },
      },
    }),
  ) as Promise<PagedCustomers>;
}

export async function getCustomer(id: string) {
  return unwrap(
    (await customersClient()).GET("/api/customers/admin/customers/{id}", { params: { path: { id } } }),
  ) as Promise<CustomerDetail>;
}

export async function getCustomerByNumber(customerNumber: string) {
  return unwrap(
    (await customersClient()).GET("/api/customers/admin/customers/by-number/{customerNumber}", {
      params: { path: { customerNumber } },
    }),
  ) as Promise<CustomerDetail>;
}

/** The customer a route's `:customerNumber` names; a legacy id throws a redirect to the number URL, query kept. */
export async function customerByKey(request: Request, key: string): Promise<CustomerDetail> {
  if (!isGuid(key)) return getCustomerByNumber(key);
  throw redirectToCanonical(request, key, customerPath((await getCustomer(key)).summary.customerNumber));
}

/** `""` from an empty input is not `null` — the API distinguishes them. */
function optional(formData: FormData, name: string): string | null {
  const value = String(formData.get(name) ?? "").trim();
  return value === "" ? null : value;
}

/** A decimal typed by a person, parsed once. Not a calculation — passed to the API as entered. */
function decimal(formData: FormData, name: string): number {
  const raw = String(formData.get(name) ?? "").trim();
  const value = Number(raw);
  if (raw === "" || !Number.isFinite(value)) throw new Error(`"${raw}" is not a number. ${name} must be an amount.`);
  return value;
}

export function missingTermsFields(formData: FormData): string[] {
  return ["tierId", "creditLimit", "paymentTermDays"].filter((field) => String(formData.get(field) ?? "").trim() === "");
}

/** Assign a tier, credit limit and payment term — one call converts the customer to B2B. */
export async function assignTerms(customerId: string, formData: FormData): Promise<void> {
  const missing = missingTermsFields(formData);
  if (missing.length > 0) throw new Error(`Tier, credit limit and payment term are all required. Missing: ${missing.join(", ")}.`);

  await unwrap(
    (await customersClient()).POST("/api/customers/admin/customers/{id}/terms", {
      params: { path: { id: customerId } },
      body: {
        tierId: String(formData.get("tierId") ?? ""),
        creditLimit: decimal(formData, "creditLimit"),
        paymentTermDays: decimal(formData, "paymentTermDays"),
        tradeTermsRequestId: optional(formData, "tradeTermsRequestId"),
        reason: optional(formData, "reason"),
      },
    }),
  );
}

export async function declineTradeTerms(requestId: string, formData: FormData): Promise<void> {
  const reason = optional(formData, "reason");
  if (!reason) throw new Error("A decline is notified to the customer and needs a reason.");
  await unwrap(
    (await customersClient()).POST("/api/customers/admin/trade-terms/{id}/decline", {
      params: { path: { id: requestId } },
      body: { reason: reason ?? null },
    }),
  );
}

/** BR-09's refusal — the two extension members its 409 carries. Read, never assumed. */
export type DemotionRefusal = { title: string; outstanding: number | null; openCreditOrders: string[] };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export function demotionRefusal(problem: unknown): DemotionRefusal | null {
  if (!isRecord(problem) || problem.status !== 409) return null;
  const outstanding =
    typeof problem.outstanding === "number"
      ? problem.outstanding
      : typeof problem.outstanding === "string" && problem.outstanding.trim() !== ""
        ? Number(problem.outstanding)
        : null;
  const orders = Array.isArray(problem.openCreditOrders) ? problem.openCreditOrders.filter((o): o is string => typeof o === "string") : [];
  return {
    title: typeof problem.title === "string" ? problem.title : "Demotion refused",
    outstanding: outstanding !== null && Number.isFinite(outstanding) ? outstanding : null,
    openCreditOrders: orders,
  };
}

/**
 * Demote to the Default Tier. Unlike the other mutations, a 409 here is not an error to throw —
 * BR-09's refusal carries the outstanding balance and open orders and belongs beside the customer
 * it describes, so it's returned as a value for the caller to render inline.
 */
export async function demoteCustomer(
  customerId: string,
  formData: FormData,
): Promise<{ refused: DemotionRefusal | null }> {
  const reason = optional(formData, "reason");
  if (!reason) throw new Error("A demotion is audit-logged and needs a reason.");

  try {
    await unwrap(
      (await customersClient()).POST("/api/customers/admin/customers/{id}/demote", {
        params: { path: { id: customerId } },
        body: { defaultTierId: String(formData.get("defaultTierId") ?? ""), reason },
      }),
    );
    return { refused: null };
  } catch (err) {
    // The parsed problem body, not the ApiError wrapper — BR-09's `outstanding`/`openCreditOrders`
    // extension fields live on the RFC 9457 document itself (err.raw), never on the error class.
    const refused = err instanceof ApiError ? demotionRefusal(err.raw) : null;
    if (refused) return { refused };
    throw err;
  }
}

export async function overrideCustomer(customerId: string, formData: FormData): Promise<void> {
  const reason = optional(formData, "reason");
  if (!reason) throw new Error("Changing a Tally-owned field is recorded as a local override and needs a reason.");

  await unwrap(
    (await customersClient()).PATCH("/api/customers/admin/customers/{id}", {
      params: { path: { id: customerId } },
      body: {
        legalName: optional(formData, "legalName"),
        gstin: optional(formData, "gstin"),
        displayName: optional(formData, "displayName"),
        email: optional(formData, "email"),
        registeredMobile: optional(formData, "registeredMobile"),
        reason,
      },
    }),
  );
}

/**
 * A pending company's billing address (`PATCH`, contracts/customers-admin.md §8). No reason while it
 * awaits approval — there is no ledger to diverge from yet — and approval refuses until there is one.
 * The API refuses an address with no state code (the place of supply).
 */
export async function setBillingAddress(customerId: string, formData: FormData): Promise<void> {
  await unwrap(
    (await customersClient()).PATCH("/api/customers/admin/customers/{id}", {
      params: { path: { id: customerId } },
      body: {
        legalName: null,
        gstin: null,
        displayName: null,
        email: null,
        registeredMobile: null,
        reason: null,
        billingAddress: {
          line1: optional(formData, "line1") ?? "",
          line2: optional(formData, "line2"),
          city: optional(formData, "city") ?? "",
          stateCode: optional(formData, "stateCode"),
          pincode: optional(formData, "pincode") ?? "",
        },
      },
    }),
  );
}

/**
 * Approve/suspend/close (`CustomerAdminController.cs`'s `ApproveCustomer`/`SuspendCustomer`/
 * `CloseCustomer`, contracts/customers-admin.md §10-12). None takes a body — `ChangeCustomerStatusHandler`
 * checks the customer's current status itself and answers 409 with `detail` when the move is invalid
 * (approve only from `pending_verification`, suspend only from `active`, close from anything but
 * `closed` — terminal, no reopen route anywhere). All three are `[Authorize("customers.administer")]`
 * on the wire; this app's Permission vocabulary has no code for that policy yet (see Detail.tsx's
 * gating comment), so the caller is left to decide what to gate on until it does.
 */
export async function approveCustomer(customerId: string): Promise<CustomerDetail> {
  return unwrap(
    (await customersClient()).POST("/api/customers/admin/customers/{id}/approve", {
      params: { path: { id: customerId } },
    }),
  ) as Promise<CustomerDetail>;
}

export async function resendCustomerInvitation(customerId: string): Promise<void> {
  return unwrap(
    (await customersClient()).POST("/api/customers/admin/customers/{id}/reinvite", {
      params: { path: { id: customerId } },
    }),
  ) as Promise<void>;
}

export async function suspendCustomer(customerId: string, reason?: string): Promise<CustomerDetail> {
  return unwrap(
    (await customersClient()).POST("/api/customers/admin/customers/{id}/suspend", {
      params: { path: { id: customerId } },
      body: { reason: reason ?? null },
    }),
  ) as Promise<CustomerDetail>;
}

/** `POST …/reinstate` — lifts a suspension (suspended → active). 409 from any other status. */
export async function reinstateCustomer(customerId: string, reason?: string): Promise<CustomerDetail> {
  return unwrap(
    (await customersClient()).POST("/api/customers/admin/customers/{id}/reinstate", {
      params: { path: { id: customerId } },
      body: { reason: reason ?? null },
    }),
  ) as Promise<CustomerDetail>;
}

export async function closeCustomer(customerId: string, reason?: string): Promise<CustomerDetail> {
  return unwrap(
    (await customersClient()).POST("/api/customers/admin/customers/{id}/close", {
      params: { path: { id: customerId } },
      body: { reason: reason ?? null },
    }),
  ) as Promise<CustomerDetail>;
}

export type StatementRunResult = { outcome: string; sent: number; skipped: number };

/** `MonthlyStatementRun.Outcome`: Disabled=0, NotDueToday=1, Due=2 — matches Ageing.tsx's `EXPLANATION` keys. */
const STATEMENT_OUTCOME_LABEL: Readonly<Record<number, string>> = {
  0: "disabled",
  1: "not_due_today",
  2: "due",
};

export async function runStatements(): Promise<StatementRunResult> {
  const data = await unwrap((await customersClient()).POST("/api/customers/admin/statements/run"));
  return {
    outcome: STATEMENT_OUTCOME_LABEL[Number(data?.outcome)] ?? "unknown",
    sent: Number(data?.sent ?? 0),
    skipped: Number(data?.skipped ?? 0),
  };
}

export async function getAgeing() {
  return unwrap((await customersClient()).GET("/api/customers/admin/ageing"));
}

export async function getDedupe() {
  return unwrap((await customersClient()).GET("/api/customers/admin/dedupe"));
}

export async function getTradeTermsQueue() {
  return unwrap((await customersClient()).GET("/api/customers/admin/trade-terms"));
}

/**
 * GAP-7 (008-reporting-dashboards T009, 003 T035): the count `CreditBreachTile` renders. Takes no
 * paging parameter — the API counts server-side rather than this screen paging the whole customer
 * list to compare `creditLimit`/`outstanding` itself (FR-017, constitution I).
 *
 * The generated route types its response `undefined` (no declared content, same shape as
 * `getAgeing`/`getDedupe` above) — `data` is parsed at runtime with `isRecord` rather than a
 * `HandWrittenType` cast (CLAUDE.md's "don't hand-cast around" rule), and a body that doesn't carry
 * a numeric `count` throws so `TileBoundary` renders it "Unreadable" rather than a fabricated 0.
 */
export async function getCreditBreaches(): Promise<{ count: number }> {
  const data = await unwrap<unknown>((await customersClient()).GET("/api/customers/admin/customers/credit-breaches"));
  if (!isRecord(data) || typeof data.count !== "number") {
    throw new Error("The credit-breaches endpoint returned no usable count.");
  }
  return { count: data.count };
}

/** One receipt Tally posted to the Customers ledger (`GET /admin/receipts`, spec §17 Finance). */
export type ReceiptRow = {
  id: string;
  customerId: string;
  customerName: string;
  documentDate: string;
  /** The receipt's size as the API states it — the ledger's negative figure, already turned round server-side. */
  amount: number | string;
  /** Tally's internal voucher id (LASTVCHID) — a key, not shown. */
  reference: string;
  /** The voucher number a person reads in Tally; null on receipts pulled before it was carried. */
  voucherNumber?: string | null;
  appliedAt: string;
  /** `CUS-000123`, the customer's URL key; null where Customers could not say. */
  customerNumber?: string | null;
};

/** `GET /admin/receipts` — server-paged, newest first, optionally one customer's (`customers.credit`). */
export async function listReceipts(params: { page: number; pageSize?: number; customerId?: string }): Promise<PagedResult<ReceiptRow>> {
  const data = (await unwrap(
    (await customersClient()).GET("/api/customers/admin/receipts", {
      params: { query: { page: params.page, pageSize: params.pageSize ?? 25, customerId: params.customerId } },
    }),
  )) as PagedResult<ReceiptRow> | undefined;
  if (!data) throw new Error("Customers returned no receipt list.");
  return data;
}

/** `GET /admin/newsletter/subscriptions` — server-paged newsletter subscribers (`customers.read`). */
export async function listNewsletterSubscribers(params: {
  page: number;
  pageSize?: number;
  q?: string;
  status?: "subscribed" | "unsubscribed";
}) {
  const data = await unwrap(
    (await customersClient()).GET("/api/customers/admin/newsletter/subscriptions", {
      params: { query: { page: params.page, pageSize: params.pageSize ?? 25, q: params.q, status: params.status } },
    }),
  );
  if (!data) throw new Error("Customers returned no newsletter subscriber list.");
  return data;
}

export type NewsletterSubscriber = Awaited<ReturnType<typeof listNewsletterSubscribers>>["items"][number];
